import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCAlert, RCCGrid, RCCHookToast, RCCRefModalProps, RCCServicesApi} from "react-common-components";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import ModalSide from "src/components/Modal/styles";
import Modal from "src/components/Modal";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import Tabs from "src/components/Tabs";
import { ISwotItem } from "src/pages/Analysis/SWOT/interface";
import swotLabels, { useIntensity, useTendencySO } from "src/pages/Analysis/SWOT/SwotLabels";
import Title from "src/components/Title";
import { IRefSwotDetailModal } from "../interface";
import { SpanStyled } from "../../styles";
import SwotItemBonds from "./bonds";
import AnalysisTraceability from "./traceability";


const SwotDetailModal: React.ForwardRefRenderFunction<IRefSwotDetailModal> = (_,ref) => {
	const { translate } = useTranslation();
	const {addToast} = RCCHookToast();
	const { setLoading } = useLoaderBackdrop();
	const [initialTab,setInitalTab] = useState<number>(0);
	const [selectedSwotItem, setSelectedSwotItem] = useState<ISwotItem>({} as ISwotItem);

	const modalref = useRef<RCCRefModalProps>(null);

	const {importance,intensity,urgency,tendencySO,tendencyWT} = swotLabels(translate);	

	

	const requestSwotItem = async(mes002Id:number) => {
		const dataController = await RCCServicesApi.getRequest({
			method: "SWOT/SelectSWOTItem",
			urlParameters: {
				mes002Id
			}
		});
		setSelectedSwotItem(dataController.data);
	};

	const actionOpenModal = async(id:number,initTab:number) => {
		try {
			setLoading(true);
			setInitalTab(initTab);
			await requestSwotItem(id);
			setLoading(false);
			modalref.current?.openModal();
		} catch (error) {
			setLoading(false);
			standardErrorMessages(error, translate("Erro ao buscar item"), addToast);
		}
	};

	useImperativeHandle(ref, () => ({
		actionOpenModal
	}));

	return (
		<>
			<Modal
				ref={modalref}
				header={{
					title: translate("Detalhes do item"),
					subtitle:`${translate("Item")}: ${selectedSwotItem.mes002Titulo}`,
					icon: {
						name: "table"
					}
				}}
				withoutForm
				footer= {{
					showSaveButton: false,
					cancelButtonText: translate("Fechar")
				}}
				styleInline={ModalSide}
			>{selectedSwotItem.mes002Id && 
				<Tabs
					name="swotDetailTab"
					tabInit={initialTab}
					options={
						[
							{
								label: translate("Dados"),
								content: 
								<RCCGrid container item>
									<RCCGrid container>
										<Title color="purple" bold>{translate("Dimensão")}:</Title>
										<SpanStyled>{selectedSwotItem.dimensionText}</SpanStyled>
									</RCCGrid>
									<RCCGrid container>
										<Title color="purple" bold>{translate("Item")}:</Title>
										<SpanStyled>{selectedSwotItem.mes002Titulo}</SpanStyled>
									</RCCGrid>
									{selectedSwotItem.mes002Identificador && 
										<RCCGrid container>
											<Title color="purple" bold>{translate("Código")}:</Title>
											<SpanStyled>{selectedSwotItem.mes002Identificador}</SpanStyled>
										</RCCGrid>
									}
									<RCCGrid container>
										<Title color="purple" bold>{translate("Importância")}:</Title>
										<SpanStyled>{importance[selectedSwotItem.mes002Importancia-1].label}</SpanStyled>
									</RCCGrid>
									<RCCGrid container>
										<Title color="purple" bold>
											{useIntensity(selectedSwotItem.mes002Secao) ? translate("Intensidade") : translate("Urgência") }:
										</Title>
										<SpanStyled>
											{useIntensity(selectedSwotItem.mes002Secao) ?
												intensity[selectedSwotItem.mes002IntensidadeUrg-1].label 
												: 
												urgency[selectedSwotItem.mes002IntensidadeUrg-1].label }
										</SpanStyled>
									</RCCGrid>
									<RCCGrid container>
										<Title color="purple" bold>{translate("Tendência")}:</Title>
										<SpanStyled>
											{useTendencySO(selectedSwotItem.mes002Secao) ? 
												tendencySO[selectedSwotItem.mes002Tendencia-1].label :
												tendencyWT[selectedSwotItem.mes002Tendencia-1].label 
											}
										</SpanStyled>
									</RCCGrid>
									<RCCGrid container>
										<Title color="purple" bold>{translate("Pontuação")}:</Title>
										<SpanStyled>
											{selectedSwotItem.mes002Pontuacao}
										</SpanStyled>
									</RCCGrid>
								</RCCGrid>
							},
							{
								label: translate("Vinculos"),
								content: 
								<RCCGrid container item>
									{selectedSwotItem.hasBonds ? 
										<SwotItemBonds swotItens={selectedSwotItem.bonds || []}/> 
										:
										<RCCAlert
											description={translate("Nenhum vinculo de item encontrado")}
										/>
									}
								</RCCGrid>
							},
							{
								label: translate("Rastreabilidade"),
								content: 
								<RCCGrid container item>
									{
										selectedSwotItem.traceability ?
											<AnalysisTraceability 
												traceability={selectedSwotItem.traceability}
											/>
											:
											<RCCAlert
												description={translate("Nenhuma rastreabilidade encontrada")}
											/>
									}
								</RCCGrid>
							}
						]
					}
				/>
				}
			</Modal>
		</>
	);
};

export default forwardRef(SwotDetailModal);
